import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../entities/access";
import { approvalTaskLabels } from "./approvalStatus";
import { shellBootstrapQuery } from "./bootstrap";
import { formatDate } from "./counterpartiesSupport";
import { dueLabels } from "./documentWorkflowSearch";
import { Notice } from "./Notice";
import { Pagination } from "./Pagination";
import type { ShellServices } from "./services";
import { TabList } from "./TabList";
import "./approvals.css";

const states = ["pending", "overdue", "decided"] as const;
const labels = {
  pending: "Ожидают",
  overdue: "Просрочены",
  decided: "Решённые",
};

export function ApprovalTasksPage({ services }: { services: ShellServices }) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const [state, setState] = useState<(typeof states)[number]>("pending");
  const [page, setPage] = useState(1);
  const allowed = hasPermission(
    { permissions: bootstrap.data?.permissions ?? [] },
    "document-workflow.approvals.view",
  );
  const list = useQuery({
    queryKey: [
      "document-workflow",
      "approval-tasks",
      bootstrap.data?.allOrganizations
        ? "all"
        : bootstrap.data?.currentScope?.id,
      state,
      page,
    ],
    queryFn: () => services.approvals!.tasks({ state, page, per_page: 10 }),
    enabled: allowed && services.approvals !== undefined,
    retry: false,
    placeholderData: keepPreviousData,
  });
  if (bootstrap.isLoading) return <p aria-busy="true">Загрузка…</p>;
  if (!allowed) return <Notice>Недостаточно прав</Notice>;
  if (!services.approvals)
    return <Notice>Сервис согласования недоступен</Notice>;
  return (
    <section className="workspace-page approval-page">
      <h1>Мне на согласование</h1>
      <TabList
        tabs={states}
        labels={labels}
        active={state}
        onSelect={(next) => {
          setState(next);
          setPage(1);
        }}
        label="Состояния заданий согласования"
        idPrefix="approval-task-tab"
        panelId="approval-task-panel"
      />
      <div
        id="approval-task-panel"
        role="tabpanel"
        aria-labelledby={`approval-task-tab-${state}`}
      >
        {list.isLoading ? <p aria-busy="true">Загружаем задания…</p> : null}
        {list.isError ? (
          <Notice>
            Не удалось загрузить задания.{" "}
            <button type="button" onClick={() => void list.refetch()}>
              Повторить
            </button>
          </Notice>
        ) : null}
        {list.isSuccess && list.data.data.length === 0 ? (
          <p>Заданий в этом списке нет</p>
        ) : null}
        {list.data && list.data.data.length > 0 ? (
          <>
            <div className="approval-table-wrap">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Документ</th>
                    <th>Уровень и круг</th>
                    <th>Срок</th>
                    <th>Состояние</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((task) => (
                    <tr key={task.id}>
                      <td data-label="Документ">
                        <Link
                          to="/modules/document-workflow/documents"
                          search={{
                            document_id: task.document.id,
                            tab: "approval",
                          }}
                        >
                          {task.document.subject}
                          {task.document.registration_number
                            ? ` · ${task.document.registration_number}`
                            : ""}
                        </Link>
                        {task.redirected_from ? (
                          <p>
                            передано вместо {task.redirected_from.display_name}
                          </p>
                        ) : null}
                      </td>
                      <td data-label="Уровень и круг">
                        Уровень {task.step_position} · Круг {task.round}
                      </td>
                      <td data-label="Срок">
                        {formatDate(task.due_at)}{" "}
                        <span className={`dw-due dw-due--${task.due_state}`}>
                          {dueLabels[task.due_state]}
                        </span>
                      </td>
                      <td data-label="Состояние">
                        {approvalTaskLabels[task.state]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={list.data.meta.current_page}
              lastPage={list.data.meta.last_page}
              total={list.data.meta.total}
              perPage={10}
              busy={list.isFetching}
              label="Страницы заданий согласования"
              onChange={setPage}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}

export function ApprovalTasksTile({ services }: { services: ShellServices }) {
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  const allowed = hasPermission(
    { permissions: bootstrap?.permissions ?? [] },
    "document-workflow.approvals.view",
  );
  const count = useQuery({
    queryKey: [
      "document-workflow",
      "approval-tasks",
      "count",
      bootstrap?.allOrganizations ? "all" : bootstrap?.currentScope?.id,
    ],
    queryFn: () => services.approvals!.tasks({ state: "pending", per_page: 1 }),
    enabled: allowed && services.approvals !== undefined,
    retry: false,
  });
  if (!allowed || !services.approvals) return null;
  return (
    <Link
      to="/modules/document-workflow/approvals/tasks"
      className="dw-dashboard-tile"
    >
      <span>На согласовании у меня</span>
      <strong>
        {count.isError ? "Недоступно" : (count.data?.meta.total ?? "…")}
      </strong>
    </Link>
  );
}
